import Axios from 'api/client';
import { TransactionPlan } from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface HasAccountId {
    accountId: number;
}

interface HasPlanId {
    planId: number;
}

interface HasPlanIds {
    planIds: number[];
}

interface EditPlanRequest extends Partial<Pick<TransactionPlan, 'next_bill_date' | 'amount'>> {
    fund_id?: number;
    appeal_id?: number | null;
}

interface FetchPlansParams extends FilterParams {
    context?: string;
}
interface VerifyPlansParams extends HasAccountId {}
interface ActivatePlanParams extends HasAccountId, HasPlanIds {}
interface EditPlanParams extends HasAccountId, HasPlanId {
    data: EditPlanRequest;
}

interface PlanStatsResponse {
    average: string;
    count: number;
    total: string;
}

interface DeletePlanParams {
    cancel_reason: string;
}

const fetchPlans = (accountId: number, params: FetchPlansParams) => {
    const { filters, ...restParams } = params;

    return Axios.post<PaginatedData<TransactionPlan>>(
        `/accounts/${accountId}/get_recurring_plans`,
        {
            ...filters,
            ...restParams,
            include: 'fund,paymentMethod',
        },
    ).then(response => response.data);
};

const fetchStats = (accountId: number) =>
    Axios.get<PlanStatsResponse>(`/accounts/${accountId}/recurring_plans/statistics`).then(
        response => response.data,
    );

const verifyPlans = ({ accountId }: VerifyPlansParams) =>
    Axios.post(`/accounts/${accountId}/recurring_plans/verify`).then(response => response.data);

const fetchPlanById = (accountId: number, planId: number) =>
    Axios.get<TransactionPlan>(`/accounts/${accountId}/recurring_plans/${planId}`).then(
        response => response.data,
    );

const deletePlan = (accountId: number, planId: number, params?: DeletePlanParams) =>
    Axios.delete(`/accounts/${accountId}/recurring_plans/${planId}`, { params }).then(
        response => response.data,
    );

const activatePlans = ({ accountId, planIds }: ActivatePlanParams) =>
    Axios.post(`/accounts/${accountId}/recurring_plans/activate`, {
        plan_ids: planIds,
    }).then(response => response.data);

const editPlan = ({ accountId, planId, data }: EditPlanParams) =>
    Axios.patch(`accounts/${accountId}/recurring_plans/${planId}`, data).then(
        response => response.data,
    );

const enableRecurringPayments = (accountId: number, code: number) =>
    Axios.patch(`/accounts/${accountId}`, {
        settings: [
            {
                code,
                active: true,
            },
        ],
    }).then(response => response.data);

export {
    fetchPlans,
    fetchPlanById,
    fetchStats,
    verifyPlans,
    deletePlan,
    activatePlans,
    editPlan,
    enableRecurringPayments,
};

export type { EditPlanRequest, FetchPlansParams };
